import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Lightning, Brain, Flame, Trophy, Clock, Target, Coins, Newspaper, Eye, Dog, ChartLineDown, Lock } from '@phosphor-icons/react';
import Navbar from '../components/Navbar';
import RankBadge, { getRankMeta } from '../components/RankBadge';
import UpgradePrompt from '../components/UpgradePrompt';
import { useAuth } from '../context/AuthContext';

const ORACLE_ICON = 'https://static.prod-images.emergentagent.com/jobs/fa067ad4-d472-4c3c-ad71-9aa2aa1140ae/images/8ba4739ef8e21669b51723b2229507cf117bffe01dff02a8f86b687a101a716b.png';
const WOLF_IMG = 'https://images.unsplash.com/photo-1564166174574-a9666f590437?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHx3b2xmJTIwaWNvbiUyMGRhcmslMjBhZ2dyZXNzaXZlfGVufDB8fHx8MTc3NjUwMjI4OXww&ixlib=rb-4.1.0&q=85&w=200';

const GameModes = () => {
  const navigate = useNavigate();
  const { user, isGuest } = useAuth();
  const [upgradePromptOpen, setUpgradePromptOpen] = useState(false);
  const [blockedMode, setBlockedMode] = useState(null);

  const handleModeClick = (mode) => {
    // Guests can only open "Learn" mode; everything else prompts signup
    if (isGuest && mode.id !== 'learn') {
      setBlockedMode(mode.title);
      setUpgradePromptOpen(true);
      return;
    }
    navigate(mode.path);
  };
  const xp = user?.xp || 0;
  const rank = user?.rank || 'Beginner';
  const nextRank = user?.next_rank;
  const xpToNext = user?.xp_to_next_rank ?? Math.max(0, 500 - xp);
  const nextThreshold = user?.next_xp_threshold ?? 500;
  const progressPct = Math.min(100, Math.round((xp / Math.max(1, nextThreshold)) * 100));
  const meta = getRankMeta(rank);

  const modes = [
    {
      id: 'learn', title: 'Learn Mode', subtitle: 'Build Your Foundation',
      description: 'Master the basics with guided, level-based challenges. React to news, make quick decisions, and learn how markets move.',
      icon: Brain, color: 'from-emerald-500 to-teal-600', bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/30',
      features: [{ icon: Clock, text: 'Timed levels' }, { icon: Target, text: 'Single stock focus' }, { icon: Trophy, text: 'Progress tracking' }],
      path: '/game-modes/learn'
    },
    {
      id: 'chaos', title: 'Chaos Mode', subtitle: 'Survive the Storm',
      description: '5 scenarios x 50 levels. Trade under extreme volatility with AI news. Survive inflation surges, wars, tech booms and more.',
      icon: Flame, color: 'from-orange-500 to-red-600', bgColor: 'bg-orange-500/10', borderColor: 'border-orange-500/30',
      features: [{ icon: Lightning, text: '5 extreme scenarios' }, { icon: Flame, text: '50 levels each' }, { icon: Coins, text: '$3,000 per scenario' }],
      path: '/game-modes/chaos'
    },
    {
      id: 'oracle', title: 'Oracle Mode', subtitle: 'Think Like Buffett',
      description: 'Analyze real companies during real-world events. Iran tensions, AI speculation, rate shocks — decide to invest, avoid, or watch.',
      customIcon: ORACLE_ICON, color: 'from-amber-500 to-yellow-600', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/30',
      features: [{ icon: Eye, text: 'Real-world events' }, { icon: ChartLineDown, text: 'Learn crash patterns' }, { icon: Coins, text: '$3,000 reward' }],
      path: '/game-modes/oracle', badge: 'NEW'
    },
    {
      id: 'wolf', title: 'Wolf Mode', subtitle: 'Ride the Hype',
      description: 'Hype stocks surge then crash. Buy during the frenzy, sell before the collapse. Master timing, greed, and fear.',
      customIcon: WOLF_IMG, color: 'from-red-600 to-rose-700', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/30',
      features: [{ icon: Lightning, text: 'Hype cycle simulation' }, { icon: Clock, text: '50 levels' }, { icon: Coins, text: '$3,000 reward' }],
      path: '/game-modes/wolf', badge: 'NEW'
    },
    {
      id: 'aibubble', title: 'AI Bubble Mode', subtitle: 'Think & Reflect',
      description: 'Analyze bubble scenarios, answer reflective questions, and get personalized AI feedback on your reasoning.',
      icon: Brain, color: 'from-blue-600 to-purple-600', bgColor: 'bg-purple-500/10', borderColor: 'border-purple-500/30',
      features: [{ icon: Brain, text: 'AI-powered feedback' }, { icon: Target, text: 'Critical thinking' }, { icon: Coins, text: '$3,000 reward' }],
      path: '/game-modes/aibubble', badge: 'NEW'
    },
    {
      id: 'news-reactor', title: 'News Reactor', subtitle: 'Earn Cash!',
      description: '100 levels of news-driven trading! Hit profit targets and earn up to $47,000 bonus cash for your main portfolio.',
      icon: Newspaper, color: 'from-yellow-500 to-orange-500', bgColor: 'bg-yellow-500/10', borderColor: 'border-yellow-500/30',
      features: [{ icon: Coins, text: 'Earn portfolio cash' }, { icon: Trophy, text: '100 levels' }, { icon: Lightning, text: 'News trading' }],
      path: '/game-modes/news-reactor'
    }
  ];

  return (
    <div className="min-h-screen noise-bg" data-testid="game-modes-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-unbounded font-bold text-white mb-4">Game Modes</h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto">6 unique ways to master the market. Earn <span className="text-gold font-bold">+200 XP</span> per mode. Reach <span className="text-yellow-400 font-bold">Master (500 XP)</span> to unlock the Indian 🇮🇳 Stock Market.</p>
        </motion.div>

        {/* Progression Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="glass-card p-5 mb-10 border border-white/10"
          data-testid="progression-card"
        >
          <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${meta.color} flex items-center justify-center text-2xl`}>
                {meta.badge}
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wide">Your Progression</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <RankBadge rank={rank} size="sm" />
                  <span className="font-mono font-bold text-gold" data-testid="gamemodes-xp">{xp} XP</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              {nextRank ? (
                <>
                  <p className="text-xs text-zinc-500">Next rank</p>
                  <p className="text-sm font-bold text-white">{nextRank} <span className="text-zinc-500 font-mono text-xs">· {xpToNext} XP to go</span></p>
                </>
              ) : (
                <p className="text-sm font-bold text-gold">🇮🇳 Indian Market Unlocked!</p>
              )}
            </div>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }} animate={{ width: `${progressPct}%` }} transition={{ duration: 0.8 }}
              className={`h-full bg-gradient-to-r ${meta.color}`}
            />
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modes.map((mode, index) => {
            const Icon = mode.icon;
            const locked = isGuest && mode.id !== 'learn';
            return (
              <motion.div key={mode.id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.08 }}
                onClick={() => handleModeClick(mode)}
                className={`relative overflow-hidden rounded-2xl ${mode.bgColor} ${mode.borderColor} border-2 p-6 cursor-pointer group hover:scale-[1.02] transition-all duration-300 ${locked ? 'opacity-70' : ''}`}
                data-testid={`${mode.id}-mode-card`}
              >
                {locked && (
                  <div className="absolute top-4 right-4 flex items-center gap-1 px-2.5 py-1 bg-black/60 backdrop-blur text-yellow-400 text-[11px] font-bold rounded-full border border-yellow-500/30" data-testid={`${mode.id}-locked-badge`}>
                    <Lock size={11} weight="fill" /> Sign up
                  </div>
                )}
                {mode.badge && !locked && (
                  <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-xs font-bold rounded-full">{mode.badge}</div>
                )}
                <div className={`absolute inset-0 bg-gradient-to-br ${mode.color} opacity-0 group-hover:opacity-10 transition-opacity`} />

                {mode.customIcon ? (
                  <img src={mode.customIcon} alt={mode.title} className="w-16 h-16 rounded-xl object-cover mb-4" />
                ) : (
                  <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${mode.color} mb-4`}>
                    <Icon size={36} weight="fill" className="text-white" />
                  </div>
                )}

                <h2 className="text-xl font-unbounded font-bold text-white mb-1">{mode.title}</h2>
                <p className={`text-sm font-medium bg-gradient-to-r ${mode.color} bg-clip-text text-transparent mb-3`}>{mode.subtitle}</p>
                <p className="text-zinc-400 text-sm mb-4">{mode.description}</p>

                <div className="space-y-3">
                  {mode.features.map((feature, i) => {
                    const FeatureIcon = feature.icon;
                    return (
                      <div key={i} className="flex items-center gap-3 text-zinc-300">
                        <FeatureIcon size={18} className="text-zinc-500" /><span className="text-sm">{feature.text}</span>
                      </div>
                    );
                  })}
                </div>

                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  className={`mt-8 w-full py-4 rounded-xl bg-gradient-to-r ${mode.color} text-white font-bold text-lg shadow-lg`}
                >
                  Play {mode.title}
                </motion.button>
              </motion.div>
            );
          })}
        </div>
      </div>
      <UpgradePrompt
        open={upgradePromptOpen}
        onClose={() => setUpgradePromptOpen(false)}
        action={`play ${blockedMode || 'this game mode'}`}
      />
    </div>
  );
};

export default GameModes;
